import { useTranslation } from 'react-i18next';
import { classSections } from '@/mocks/students';

interface StudentFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedClass: string;
  onClassChange: (value: string) => void;
  showInactive: boolean;
  onToggleInactive: (value: boolean) => void;
  showIncidentsOnly: boolean;
  onToggleIncidents: (value: boolean) => void;
  incidentCount: number;
  onAddStudent: () => void;
}

export default function StudentFilters({
  search,
  onSearchChange,
  selectedClass,
  onClassChange,
  showInactive,
  onToggleInactive,
  showIncidentsOnly,
  onToggleIncidents,
  incidentCount,
  onAddStudent,
}: StudentFiltersProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder={t('students.searchStudents')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="xl-input pl-9 pr-4 w-full text-sm"
          />
        </div>

        {/* Class Filter */}
        <div className="relative">
          <select
            value={selectedClass}
            onChange={(e) => onClassChange(e.target.value)}
            className="xl-input pr-10 pl-4 w-52 text-sm appearance-none cursor-pointer"
          >
            <option value="">{t('students.allClasses')}</option>
            {classSections.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        </div>

        {/* Incidents Only Toggle */}
        <button
          type="button"
          onClick={() => onToggleIncidents(!showIncidentsOnly)}
          className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-label font-medium transition-colors cursor-pointer whitespace-nowrap border ${
            showIncidentsOnly
              ? 'bg-red-50 border-red-300 text-red-600'
              : 'bg-background-100 border-background-200 text-foreground-500 hover:bg-background-200'
          }`}
        >
          <i className={`text-sm ${showIncidentsOnly ? 'ri-alert-fill' : 'ri-alert-line'}`} />
          {showIncidentsOnly ? t('students.showingIncidentsOnly') : t('students.incidentsOnly', { count: incidentCount })}
        </button>

        {/* Inactive Toggle */}
        <button
          type="button"
          onClick={() => onToggleInactive(!showInactive)}
          className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-label font-medium transition-colors cursor-pointer whitespace-nowrap border ${
            showInactive
              ? 'bg-semantic-warning-bg border-semantic-warning/30 text-semantic-warning'
              : 'bg-background-100 border-background-200 text-foreground-500 hover:bg-background-200'
          }`}
        >
          <i className={`text-sm ${showInactive ? 'ri-eye-line' : 'ri-eye-off-line'}`} />
          {showInactive ? t('students.showingInactive') : t('students.hideInactive')}
        </button>
      </div>

      {/* Add Student */}
      <button
        onClick={onAddStudent}
        className="inline-flex items-center gap-2 h-10 px-4 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 active:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-user-add-line" />
        {t('students.addStudent', { defaultValue: 'Add Student' })}
      </button>
    </div>
  );
}